// Where this matter came from.
//
// Small on purpose. The value is not in this card — it is in the
// Referral sources report, which is only as good as how often somebody
// fills this in. So the job here is to make recording it a few seconds'
// work and to make an unrecorded matter look unrecorded.
//
// "Nobody has said" and "came to us directly" are kept apart. A blank
// already means the first; the second has to be chosen. Collapsing them
// would turn every matter nobody got round to into a marketing win.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

const KINDS = [
  ["attorney", "Attorney"],
  ["firm", "Firm"],
  ["former_client", "Former client"],
  ["medical_provider", "Medical provider"],
  ["marketing", "Marketing"],
  ["other", "Other"],
];

export function ReferralCard({ matterId, showNotification }) {
  const [current, setCurrent] = useState(null);
  const [sources, setSources] = useState([]);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", kind: "attorney" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ref, srcs] = await Promise.all([
        apiFetch(`/api/matters/${matterId}/referral`),
        apiFetch("/api/referral-sources"),
      ]);
      setCurrent(ref);
      setSources(srcs.data || []);
    } catch {
      setCurrent({ source: null, is_direct: false });
    }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setSaving(true);
    try {
      await apiFetch(`/api/matters/${matterId}/referral`, {
        method: "PUT", body: JSON.stringify(body),
      });
      setEditing(false);
      await load();
    } catch (e) {
      showNotification && showNotification(e.message, "error");
    }
    setSaving(false);
  };

  const addSource = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      const created = await apiFetch("/api/referral-sources", {
        method: "POST", body: JSON.stringify(draft),
      });
      setAdding(false);
      setDraft({ name: "", kind: "attorney" });
      await save({ source_id: created.id });
    } catch (e) {
      showNotification && showNotification(e.message, "error");
      setSaving(false);
    }
  };

  if (current === null) return null;

  const label = current.source
    ? current.source.name
    : current.is_direct ? "Came to the firm directly" : null;

  return React.createElement("div", {
    style: { background: "#fff", borderRadius: "12px", padding: "16px 20px",
             border: `1px solid ${C.warmGray}`, marginBottom: "20px" },
  },
    React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", alignItems: "center", gap: "10px",
               flexWrap: "wrap" },
    },
      React.createElement("h2", {
        style: { fontSize: "12px", color: C.textMuted, textTransform: "uppercase",
                 letterSpacing: "0.08em", fontWeight: 600, margin: 0,
                 minWidth: "110px" },
      }, "Referred by"),

      React.createElement("div", { style: { flex: 1, minWidth: "160px" } },
        label
          ? React.createElement("span", {
              style: { fontSize: "14px", color: C.navy, fontWeight: 600 },
            }, label,
              current.source?.organization && React.createElement("span", {
                style: { color: C.textMuted, fontWeight: 400 },
              }, ` · ${current.source.organization}`))
          // Not styled as an error. It is a gap, and it is extremely
          // common early on — the report already says how much of the
          // book is unrecorded.
          : React.createElement("span", {
              style: { fontSize: "13.5px", color: C.textLight },
            }, "Not recorded"),
      ),

      React.createElement("button", {
        onClick: () => setEditing((v) => !v),
        style: { border: `1px solid ${C.warmGray}`, background: "#fff",
                 borderRadius: "6px", padding: "5px 12px", fontSize: "12px",
                 fontWeight: 600, color: C.navy, cursor: "pointer" },
      }, editing ? "Cancel" : label ? "Change" : "Record"),
    ),

    editing && React.createElement("div", {
      style: { marginTop: "14px", paddingTop: "13px",
               borderTop: `1px solid ${C.warmGray}55` },
    },
      !adding && React.createElement("div", {
        className: "wrap-row",
        style: { display: "flex", gap: "8px", flexWrap: "wrap",
                 alignItems: "center" },
      },
        React.createElement("select", {
          defaultValue: current.source?.id || "",
          onChange: (e) => e.target.value && save({ source_id: Number(e.target.value) }),
          disabled: saving,
          style: { padding: "7px 10px", fontSize: "13px", borderRadius: "6px",
                   border: `1px solid ${C.warmGray}`, background: "#fff",
                   color: C.navy, minWidth: "200px" },
        },
          React.createElement("option", { value: "" }, "— Pick a source —"),
          ...sources.map((s) => React.createElement("option",
            { key: s.id, value: s.id },
            s.organization ? `${s.name} (${s.organization})` : s.name)),
        ),
        React.createElement("button", {
          onClick: () => setAdding(true), disabled: saving,
          style: { border: `1px solid ${C.warmGray}`, background: "#fff",
                   borderRadius: "6px", padding: "6px 12px", fontSize: "12px",
                   fontWeight: 600, color: C.navy, cursor: "pointer" },
        }, "New source"),
        React.createElement("button", {
          onClick: () => save({ is_direct: true }), disabled: saving,
          style: { border: "none", background: "transparent", padding: "6px 4px",
                   fontSize: "12px", color: C.navy, cursor: "pointer",
                   textDecoration: "underline" },
        }, "Came to us directly"),
      ),

      adding && React.createElement("div", {
        className: "wrap-row",
        style: { display: "flex", gap: "8px", flexWrap: "wrap",
                 alignItems: "center" },
      },
        React.createElement("input", {
          autoFocus: true, placeholder: "Name",
          value: draft.name,
          onChange: (e) => setDraft((d) => ({ ...d, name: e.target.value })),
          style: { padding: "7px 10px", fontSize: "13px", borderRadius: "6px",
                   border: `1px solid ${C.warmGray}`, minWidth: "190px" },
        }),
        React.createElement("select", {
          value: draft.kind,
          onChange: (e) => setDraft((d) => ({ ...d, kind: e.target.value })),
          style: { padding: "7px 10px", fontSize: "13px", borderRadius: "6px",
                   border: `1px solid ${C.warmGray}`, background: "#fff",
                   color: C.navy },
        }, ...KINDS.map(([v, l]) =>
          React.createElement("option", { key: v, value: v }, l))),
        React.createElement("button", {
          onClick: addSource, disabled: saving || !draft.name.trim(),
          style: { border: "none", background: C.navy, color: "#fff",
                   borderRadius: "6px", padding: "7px 15px", fontSize: "12.5px",
                   fontWeight: 600, cursor: "pointer" },
        }, saving ? "Saving…" : "Add and use"),
        React.createElement("button", {
          onClick: () => setAdding(false),
          style: { border: "none", background: "transparent", fontSize: "12px",
                   color: C.textMuted, cursor: "pointer" },
        }, "Back"),
      ),
    ),
  );
}
